import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { FiChevronDown } from 'react-icons/fi';
import { Header } from '@/components/custom/header/header';
import { FormCheck } from '@/components/custom/form';
import usePermission from '@/hooks/usePermission';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { ProjectsProvider } from '../../provider';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { CardAvatarStack } from '@/components/ui/card-avatar-stack';
import { Link } from 'react-router-dom';
import { Dropdown } from '@/components/custom/dropdown/dropdown';
import { confirm } from '@/lib/confirm';
import { useTranslation } from 'react-i18next';
import { Dashboard, DashboardContent } from '@/layouts/dashboard';

export function ViewGroupProject(props) {
  return (
    <ProjectsProvider>
      <ScreenContentGroupProject {...props} />
    </ProjectsProvider>
  );
}

export function ScreenContentGroupProject(props) {
  const { t } = useTranslation();
  const client = useClient();
  const { id, uri } = props;
  const [groupedProject, setGroupedProject] = useState({});
  const [participantsQuantity, setParticipantsQuantity] = useState();
  const [loading, setLoading] = useState(true);
  const [filteredProjects, setFilteredProjects] = useState([]);

  const getGroupProject = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`project_groups/${id}`);
    if (error) {
      toast.error('Erro ao buscar informações sobre o Grupo de Projetos.');
    } else {
      setGroupedProject(data);
      setLoading(false);
      setParticipantsQuantity(data['user-count']);
    }
  };

  const filters = useFormik({
    initialValues: {
      approvedProjects: false,
      projectsInProgress: false
    }
  });

  useEffect(() => {
    getGroupProject();
  }, []);

  useEffect(() => {
    let newProjects = groupedProject.projects;
    if (filters.values.approvedProjects === true) {
      newProjects = newProjects.filter(p => p.approved === true);
    }
    if (filters.values.projectsInProgress === true) {
      newProjects = newProjects.filter(p => p.approved === null || p.approved === false);
    }
    setFilteredProjects(newProjects);
  }, [filters.values, groupedProject]);

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/projetos/grupos',
        name: 'Projetos',
        isActive: false
      },
      {
        route: uri,
        name: `Grupos de ${groupedProject?.projects?.[0]?.name}`,
        isActive: true
      }
    ]
  };

  return (
    <ProjectsProvider>
      <Dashboard>
        <Header />

        {groupedProject && usePermission('Visualizar lista de projetos', 'Projetos') && (
          <DashboardContent
            variant="plain"
            breadcrumbs={breadcrumbs}
          >
            <div className="filter-bar">
              <div className="filter-bar__inner">
                <FormCheck
                  label={t('metrics.approvedProjects')}
                  checked={filters.values.approvedProjects}
                  onCheckedChange={checked => {
                    filters.setFieldValue('approvedProjects', checked);
                    filters.setFieldValue('projectsInProgress', false);
                  }}
                />
                <FormCheck
                  label={t('metrics.projectsInProgress')}
                  checked={filters.values.projectsInProgress}
                  onCheckedChange={checked => {
                    filters.setFieldValue('projectsInProgress', checked);
                    filters.setFieldValue('approvedProjects', false);
                  }}
                />
              </div>

              <div className="filter-bar__box">
                <span>
                  <strong>
                    {groupedProject?.projects?.length} {t('projects.sidebarGroups')}
                  </strong>
                  <br />
                  {participantsQuantity} {t('sideBar.participants')}
                </span>
              </div>
            </div>

            {loading && <Loader />}

            {filteredProjects?.length === 0 && !loading && (
              <EmptyState
                type="project"
                title={t('projects.newProjects')}
                text={t('emptyState.createNewProjectInteract')}
              />
            )}

            {!loading && (
              <div className="tab__cards">
                {filteredProjects?.map(p => (
                  <div
                    className="card"
                    key={p.id}
                  >
                    <div className="card__header">
                      <div className="card__inner-header">
                        <h3 className="card__title">{p.name}</h3>
                      </div>

                      {p.approved ? <span className="badge badge--success badge--tiny">{t('exams.statusInApproved')}</span> : <span className="badge badge--primary badge--tiny">{t('exams.inProgress')}</span>}
                    </div>

                    <div className="card__body">
                      <div className="card__columns">
                        <div className="card__avatar">
                          <h4 className="card__avatar-title">{t('projects.participants')}</h4>
                          <CardAvatarStack
                            data={p.users}
                            modalTitle={`${t('projects.titleProjectParticipants')} ${p.name}`}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card__footer">
                      <div
                        className="btn-group btn-group--with-dropdown"
                        role="group"
                        aria-label="Ações"
                      >
                        {p['project-users']?.some(projectUser => projectUser.user_id === session.user.id) ? (
                          <Link
                            to={`/projetos/${p.id}`}
                            type="button"
                            className="btn btn--outline btn--small"
                          >
                            {t('button.accessProject')}
                          </Link>
                        ) : (
                          <Link
                            to={`/projetos/grupos/${id}/${p.id}/sobre`}
                            type="button"
                            className="btn btn--outline btn--small"
                          >
                            {t('button.detailsProject')}
                          </Link>
                        )}
                        {usePermission('Editar projetos', 'Projetos') || usePermission('Excluir projetos', 'Projetos') ? (
                          <Dropdown className="btn--small btn btn--outline">
                            <Dropdown.Toggle>
                              <FiChevronDown />
                            </Dropdown.Toggle>

                            <Dropdown.Content>
                              {usePermission('Excluir projetos', 'Projetos') && (
                                <Dropdown.Item
                                  onClick={async () => {
                                    const confirmed = await confirm({
                                      title: t('projects.warningDeleteProject'),
                                      description: t('projects.warningDeleteCannotBeUndoneText'),
                                      confirmText: t('button.yes'),
                                      cancelText: t('button.no')
                                    });
                                    if (confirmed) {
                                      deletePersonalizedProject(id);
                                    }
                                  }}
                                >
                                  {t('textsCommon.delete')}
                                </Dropdown.Item>
                              )}
                            </Dropdown.Content>
                          </Dropdown>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DashboardContent>
        )}
      </Dashboard>
    </ProjectsProvider>
  );
}
